import React, { useState } from 'react';
import { MapPin, Upload, ChevronLeft, ChevronRight, } from 'lucide-react';
import StatusBadge from '../StatusBadge';
import IntelSubmissionModal from '../IntelSubmissionModal';

function LocationInfoWindow({
  selected,
  currentImageIndex,
  onNextImage,
  onPrevImage,
  onOpenGoogleMaps,
  onOpenArcGIS,
  user
}) {
  const [showIntelModal, setShowIntelModal] = useState(false);
  const daysSince = selected.daysSinceVerification ?? 0;
  const verificationStatus = selected.verificationStatus || selected.status;
  const needsVerify = verificationStatus === "aging";
  const isStale = verificationStatus === "stale";

  return (
    <div
      className="location-intel-window text-white w-80 max-h-[85vh] flex flex-col"
      style={{ fontFamily: "'Inter Tight', sans-serif" }}
    >
      {selected.images && selected.images.length > 0 && (
        <div className="relative flex-shrink-0">
          <img
            src={selected.images[currentImageIndex]}
            alt={selected.name}
            className="intel-window-image w-full h-48 object-cover"
          />
          {selected.images.length > 1 && (
            <>
              <button
                onClick={onPrevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-70"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={onNextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-70"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
            {currentImageIndex + 1} / {selected.images.length}
          </div>
        </div>
      )}

      <div className="p-3 overflow-y-auto flex-1 min-h-0">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-bold text-lg">{selected.name}</h3>
          <StatusBadge
            status={verificationStatus}
            verificationStatus={verificationStatus}
            lastVerified={selected.lastVerified}
            daysSinceVerification={daysSince}
          />
        </div>

        <div className="mb-3">
          <span className="text-sm font-medium text-gray-700">Type: </span>
          <span className="text-sm text-gray-600">{selected.type || 'Unknown'}</span>
        </div>

        {selected.observationCounts && (
          <div className="mb-4">
            <h4 className="font-medium text-sm mb-2">Observations Summary:</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              {selected.observationCounts.buildingStanding > 0 && (
                <li>• Building still standing ({selected.observationCounts.buildingStanding})</li>
              )}
              {selected.observationCounts.exteriorAccess > 0 && (
                <li>• Exterior access observed ({selected.observationCounts.exteriorAccess})</li>
              )}
              {selected.observationCounts.securityPresence > 0 && (
                <li>• Security presence observed ({selected.observationCounts.securityPresence})</li>
              )}
              {selected.observationCounts.maintenanceSigns > 0 && (
                <li>• Signs of maintenance ({selected.observationCounts.maintenanceSigns})</li>
              )}
              {selected.observationCounts.redevelopmentSigns > 0 && (
                <li>• Signs of redevelopment ({selected.observationCounts.redevelopmentSigns})</li>
              )}
            </ul>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
          <MapPin size={16} />
          <span>
            {selected.lat?.toFixed(6) || '0.000000'}, {selected.lng?.toFixed(6) || '0.000000'}
          </span>
        </div>

        <div className="flex gap-2 mb-3">
          <button
            onClick={(e) => onOpenGoogleMaps(e, selected.lat, selected.lng)}
            className="classified-secondary-button flex-1 px-3 py-1 text-center"
          >
            Google Maps
          </button>
          <button
            onClick={(e) => onOpenArcGIS(e, selected.lat, selected.lng)}
            className="classified-secondary-button flex-1 px-3 py-1 text-center"
          >
            ArcGIS
          </button>
        </div>

        {needsVerify && !isStale && (
          <div className="classified-intel-alert classified-intel-alert--aging mb-3 p-3">
            <p className="text-sm font-medium">Due for a fresh update</p>
            <p className="text-xs mt-1">
              Last verified {daysSince} days ago
            </p>
          </div>
        )}

        {isStale && (
          <div className="classified-intel-alert classified-intel-alert--solid mb-3 p-3">
            <p className="text-sm font-medium">Last update is 120+ days old</p>
            <p className="text-xs mt-1">
              Last verified {daysSince} days ago.
              Details may have changed.
            </p>
          </div>
        )}

        <div className="classified-intel-note mb-4 p-3 text-xs">
          <p className="font-medium">Community-reported details</p>
          <p className="mt-1">Conditions may change. No access, safety, or abandonment is guaranteed.</p>
        </div>

        <button
          onClick={() => setShowIntelModal(true)}
          className="classified-primary-button w-full py-2 px-4 flex items-center justify-center gap-2"
        >
          <Upload size={16} />
          Share an update
        </button>
      </div>

      <IntelSubmissionModal
        isOpen={showIntelModal}
        onClose={() => setShowIntelModal(false)}
        locationId={selected.id}
        userId={user?.uid}
        userName={user?.name ?? 'Anonymous'}
        userEmail={user?.email}
        userStatus={user?.status}
      />
    </div>
  );
}

export default LocationInfoWindow;
